import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog-mobile-optimized";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, CheckCircle, Loader2 } from "lucide-react";
import { resetPasswordSchema } from "@/lib/validation/authSchemas";
import { hapticSuccess, hapticError } from "@/utils/haptics";
import { Pill, PrimaryCTA, SecondaryCTA, IconStamp } from "@/components/ui/atrium";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentMode?: "signin" | "signup";
}

export const ForgotPasswordDialog = ({
  open,
  onOpenChange,
  currentMode = "signin",
}: ForgotPasswordDialogProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const validation = resetPasswordSchema.safeParse({ email });
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    setLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/reset-password?mode=${currentMode}`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
      if (error) throw error;

      hapticSuccess();
      setSuccess(true);
      toast({ title: "Check your email", description: "We've sent you a password reset link." });
    } catch (error: any) {
      hapticError();
      setError(error.message || "Failed to send reset email");
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setSuccess(false);
    setError("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card/95 backdrop-blur-md border border-border/60 rounded-3xl p-0 max-w-md">
        <div className="p-7 md:p-8">
          <DialogHeader className="space-y-3 text-left mb-6">
            <Pill>Recovery</Pill>
            <DialogTitle className="font-sans font-medium text-2xl md:text-3xl tracking-[-0.02em] text-foreground leading-tight">
              Reset your <span className="font-serif italic font-light text-primary">password</span>
            </DialogTitle>
            <DialogDescription className="text-sm md:text-base text-foreground/70 leading-relaxed">
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="flex flex-col items-center gap-5 py-4 text-center">
              <IconStamp tone="success" size="lg">
                <CheckCircle />
              </IconStamp>
              <div className="space-y-1.5">
                <p className="font-sans font-medium text-lg text-foreground">Check your email</p>
                <p className="text-sm text-foreground/70">
                  We've sent a password reset link to{" "}
                  <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>
              <PrimaryCTA onClick={handleClose} className="w-full mt-2">
                Done
              </PrimaryCTA>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-xs font-medium text-foreground/75">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/55" />
                  <Input
                    id="reset-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-12 rounded-2xl bg-background/80 border-border/60 focus-visible:ring-primary/30"
                    disabled={loading}
                    required
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>

              <div className="flex gap-3 pt-1">
                <SecondaryCTA type="button" onClick={handleClose} disabled={loading} className="flex-1">
                  Cancel
                </SecondaryCTA>
                <PrimaryCTA type="submit" disabled={loading} className="flex-1">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send reset link <span aria-hidden>→</span>
                    </>
                  )}
                </PrimaryCTA>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
